import _ from 'lodash';
import { fromJS, Map } from 'immutable';
import { NO_KITCHEN_ISLAND_ID, KITCHEN_ISLAND_ID } from 'domain/Options/generators/kitchenIslands';

import { WALL_PANEL_ID, WALL_EDGING_STRIP_CUSTOMIZABLE_ID } from 'domain/Options/generators/splashbacks';
import { SELECTION_TYPE_DEFAULT, SELECTION_TYPE_USER, SELECTION_TYPE_AUTOMATIC } from 'domain/Options/selectionTypes';

import {
  OPTIONS_LOADED,
  SELECT_MATERIAL,
  SELECT_EXPRESSION,
  SELECT_COVER_PANEL,
  SELECT_EDGE,
  SELECT_WALL_LOCATION,
  SELECT_KITCHEN_ISLAND_WALL_LOCATION,
  SELECT_KITCHEN_ISLAND,
  SELECT_THICKNESS,
  SELECT_SPLASHBACK,
  SELECT_WALL_PANEL_EXPRESSION,
  SELECT_WALL_PANEL_MATERIAL,
  SELECT_WALL_EDGING_STRIP_MATERIAL,
  SELECT_WALL_EDGING_STRIP_CUSTOMIZABLE_MATERIAL,
  SELECT_NO_MATERIAL_WALL_PANEL_MATERIAL,
  SELECT_SINK_MATERIAL,
  SELECT_SINK_MODEL,
  SELECT_SINK_FASTENING,
  SELECT_DRAINER_GROOVES,
  CHANGE_SHAPE,
  NO_MATERIAL_CHANGE_SHAPE,
  RESET_PLAN,
  CHANGE_CUTOUTS,
  CHANGE_FREESTANDING_HOBS,
  PARAMETER_SEEN,
  CHANGE_SINGLE_CUTS,
  CHANGE_DOUBLE_CUTS,
  CHANGE_TRIPLE_CUTS,
  CHANGE_HIGH_CABINET_JOINTS,
  CHANGE_ROUNDED_CORNERS,
  CHANGE_INVERTED_CORNERS,
  CHANGE_TAP_HOLES,
  RESET_NUMERICAL_PARAMETER,
  NO_MATERIAL_RESET_NUMERICAL_PARAMETER,
  RESET_WORKTOP_SHAPE_MEASUREMENTS,
  RESET_PREVIOUS_SINK_MODEL,
  SET_PLAN_NAME,
  CHANGE_WALL_PANEL_ROUND_CUTS_SMALL,
  CHANGE_WALL_PANEL_ROUND_CUTS_BIG,
  CHANGE_WALL_PANEL_SQUARE_CUTS,
  CHANGE_WALL_PANEL_SINGLE_CUTS,
  CHANGE_WALL_PANEL_DOUBLE_CUTS,
  CHANGE_WALL_PANEL_TRIPLE_CUTS,
  SET_PLAN_CODE,
  SELECT_KITCHEN_ISLAND_MATERIAL,
  SELECT_NO_MATERIAL_KITCHEN_ISLAND_MATERIAL,
  SELECT_KITCHEN_ISLAND_EXPRESSION,
  SELECT_KITCHEN_ISLAND_COVER_PANEL,
  SELECT_KITCHEN_ISLAND_EDGE,
  SELECT_KITCHEN_ISLAND_THICKNESS,
  SELECT_KITCHEN_ISLAND_SAME_AS_WORKTOP,
  SELECT_NO_MATERIAL_KITCHEN_ISLAND_SAME_AS_WORKTOP,
  SELECT_OVERHANG,
  SELECT_OVERHANG_SIDE,
  SELECT_NO_MATERIAL_OVERHANG,
  SELECT_WALL_PANEL_MATCH_WORKTOP,
  SET_DOUBLE_SIDED,
  SELECT_NO_MATERIAL_WALL_PANEL_MATCH_WORKTOP,
  SET_KITCHEN_ISLAND_DOUBLE_SIDED,
  SELECT_KITCHEN_ISLAND_SPLASH_BACK,
  SELECT_KITCHEN_ISLAND_OVERHANG,
  SELECT_KITCHEN_ISLAND_OVERHANG_SIDE,
  CHANGE_KITCHEN_ISLAND_SINGLE_CUTS,
  CHANGE_KITCHEN_ISLAND_ROUNDED_CORNERS,
  CHANGE_KITCHEN_ISLAND_SQUARE_CUTS,
} from '../constants';

import * as parameters from '../planParameters';

export const planInitialState = fromJS(_.fromPairs([
  ...(parameters.objectParameters.map((name) => [name, { value: {} }])),
  ...(parameters.numericParameters.map((name) => [name, { value: 0, selectionType: SELECTION_TYPE_DEFAULT }])),
  [parameters.sinkMaterial, { value: { id: 'noSink', models: [] } }],
  [parameters.drainerGrooves, { value: { id: 'none' } }],
  [parameters.plannedShape, { value: {} }],
  [parameters.kitchenIsland, { measurements: { a: null, b: null } }],
  ['conflictMode', false],
  ['hasChanges', false],
  ['lastActionUpdatedPlan', false],
  ['kitchenIslandSameAsWorktop', true],
  ['doubleSided', false],
  ['kitchenIslandDoubleSided', false],
  ['kitchenIslandSplashBack', false],
  ['wallPanelMatchWorktop', false],
]));

export function setParameter(state, parameter, value, selectionType, extra) {
  try{
    //console.log("parameter: "+parameter+" value: "+value+" selectionType: "+ selectionType+" extra: "+extra)
    return state.mergeIn(['plan', parameter], fromJS({
      value: isNaN(value) ? (value || null) : value,
      selectionType,
      ...extra,
    }));
  }
  catch(error){
    console.error('Plan load ERROR', error);
  }
}

export const getParameterId = (state, parameter) => state.getIn(['plan', parameter, 'value', 'id']);
export const getSelectionType = (state, parameter) => state.getIn(['plan', parameter, 'selectionType']);

const searchById = (state, path, id) => {
  const search = state.getIn(path);
  return id && search && search.find((option) => option.get('id') === id);
};

export const findById = (state, path, id) => searchById(state, ['options', path], id);
export const findChildById = (state, parameter, path, id) => searchById(state, ['plan', parameter, 'value', path], id);

const selectParameter = (parameter) => (state, value, selectionType) => setParameter(state, parameter, value, selectionType);

const selectOptionById = (path, setterOrParameterName, parentParameterName = null) => {
  const setter = typeof setterOrParameterName === 'string' ? selectParameter(setterOrParameterName) : setterOrParameterName;

  return (state, id, selectionType, ...additionalArgs) => {
    const value = parentParameterName ? findChildById(state, parentParameterName, path, id) : findById(state, path, id);
    return setter(state, value, selectionType, ...additionalArgs);
  };
};

const resetNumericalParameter = (state, parameter) => setParameter(state, parameter, 0, SELECTION_TYPE_DEFAULT);
const noMaterialresetNumericalParameter = (state, parameter) => setParameter(state, parameter, 0, SELECTION_TYPE_DEFAULT);

const resetWorktopShapeMeasurements = (state, shapeId) => {
  const shape = findById(state, 'shapes', shapeId);
  const shapeToUse = shape || Map();
  const dimensions = shapeToUse.get('dimensions');
  let measurementsOrDefault;
  if (dimensions) {
    measurementsOrDefault = dimensions.map((dimension, dimensionId) => {
      return dimension.get('defaultValue');
    });
  }
  return state.setIn(['shapeFields'], Map()).setIn(['plan', 'plannedShape', 'measurements'], measurementsOrDefault);
};

const getPreviousSelection = (state, parameterValue, storage, defaultPath) => {
  const parameterValueToUse = parameterValue || Map();
  const parameterId = parameterValueToUse.get('id');
  const previousSelection = state.getIn(['plan', storage, parameterId]);
  return { value: previousSelection || parameterValueToUse.getIn([defaultPath, 0]), selectionType: previousSelection ? SELECTION_TYPE_USER : SELECTION_TYPE_DEFAULT };
};

function selectDefaultOptions(state) {
  const actions = [
    [selectMaterial, ['options', 'materials', 1]],
    [selectEdgeId, ['options', 'edges', 0, 'id']],
    [selectThicknessId, ['plan', parameters.expression, 'value', 'thicknesses', 0, 'id']],
    [selectSinkMaterialId, ['options', 'sinks', 0, 'id']],
    [selectSinkFasteningId, 'topMounted'],
    [selectDrainerGroovesId, 'none'],
    [changeShape, ['options', 'shapes', 0, 'id'], {}, false],
    [selectKitchenIslandId, NO_KITCHEN_ISLAND_ID],
    [selectSplashback, ['options', 'splashbacks', 0]],
    [selectWallPanelMaterial, ['options', 'wallPanels', 0], false],
    [selectWallEdgingStripMaterial, ['options', 'wallEdgingStrip', 0], true],
    [selectWallEdgingStripCustomizableMaterial, ['options', 'wallEdgingStripCustomizable', 0], true],
    [selectKitchenIslandMaterial, ['options', 'materials', 1], true],
    [selectKitchenIslandEdgeId, ['options', 'edges', 0, 'id'], true],
    [selectKitchenIslandThicknessId, ['plan', parameters.kitchenIslandExpression, 'value', 'thicknesses', 0, 'id'], true],
    [selectOverhang, ['options', 'overhangs', 0]],
    [selectOverhangSide, ['options', 'shapes', 0, 'overhangSides', 0]],
    [selectKitchenIslandOverhang, ['options', 'kitchenIslandOverhangs', 0]],
    [selectKitchenIslandOverhangSide, ['options', 'kitchenIslands', 1, 'overhangSides', 0]],
  ];

  return state.withMutations((tempState) => {
    return actions
      .reduce((prevState, [select, option, ...additionalArgs]) => {
        const idOrOption = typeof option === 'object' ? prevState.getIn(option) : option;
        return select(prevState, idOrOption, SELECTION_TYPE_DEFAULT, ...additionalArgs);
      }, tempState)
      .setIn(['plan', 'hasChanges'], false);
  });
}

export function changeShape(state, shapeId, selectionType, measurements, flipped) {
  const currentShapeId = getParameterId(state, parameters.plannedShape);
  const shape = findById(state, 'shapes', shapeId);

  let stateToUse = state;
  const shapeToUse = shape || Map();
  if (currentShapeId !== shapeId) {
    // Reset wall location selection
    const tmpState = selectWallLocation(state, shapeToUse.getIn(['wallLocations', 0]), SELECTION_TYPE_DEFAULT);
    // Reset overhang side selection
    stateToUse = selectOverhangSide(tmpState, shapeToUse.getIn(['overhangSides', 0]), SELECTION_TYPE_DEFAULT);
  }
  const dimensions = shapeToUse.get('dimensions');
  let measurementsOrDefault;
  if (dimensions) {
    measurementsOrDefault = dimensions.map((dimension, dimensionId) => {
      if (dimension.get('constant')) {
        return dimension.get('defaultValue');
      } else {
        return measurements[dimensionId] || dimension.get('defaultValue');
      }
    });
  }

  return setParameter(stateToUse, parameters.plannedShape, shape, selectionType, {
    measurements: measurementsOrDefault,
    flipped,
  }).setIn(['shapesFlipped', shapeId], flipped);
}

export function noMaterialChangeShape(state, shapeId, selectionType, measurements, flipped) {
  const currentShapeId = getParameterId(state, parameters.plannedShape);
  const shape = findById(state, 'shapes', shapeId);

  let stateToUse = state;
  const shapeToUse = shape || Map();
  if (currentShapeId !== shapeId) {
    // Reset wall location selection
    const tmpState = selectWallLocation(state, shapeToUse.getIn(['wallLocations', 0]), SELECTION_TYPE_DEFAULT);
    // Reset overhang side selection
    stateToUse = selectOverhangSide(tmpState, shapeToUse.getIn(['overhangSides', 0]), SELECTION_TYPE_DEFAULT);
  }
  const dimensions = shapeToUse.get('dimensions');
  let measurementsOrDefault;
  if (dimensions) {
    measurementsOrDefault = dimensions.map((dimension, dimensionId) => {
      if (dimension.get('constant')) {
        return dimension.get('defaultValue');
      } else {
        return measurements[dimensionId] || dimension.get('defaultValue');
      }
    });
  }
  return setParameter(stateToUse, parameters.plannedShape, shape, selectionType, {
    measurements: measurementsOrDefault,
    flipped,
  }).setIn(['shapesFlipped', shapeId], flipped);
}

export const selectWallLocationId = selectOptionById('wallLocations', parameters.wallLocation, parameters.plannedShape);
export const selecKitchenIslandWallLocationId = selectOptionById('kitchenIslandWallLocations', parameters.kitchenIslandWallLocation, parameters.kitchenIsland);
export const selectWallLocation = selectParameter(parameters.wallLocation);
export const selectKitchenIslandWallLocation = selectParameter(parameters.kitchenIslandWallLocation);
export const selectKitchenIslandId = selectOptionById('kitchenIslands', selectKitchIsland);
export const selectMaterialId = selectOptionById('materials', selectMaterial);
export const selectKitchenIslandMaterialId = selectOptionById('materials', selectKitchenIslandMaterial);
export const selectNoMaterialKitchenIslandMaterialId = selectOptionById('materials', selectKitchenIslandMaterial)
export const selectKitchenIslandExpressionId = selectOptionById('expressions', selectKitchenIslandExpression, parameters.kitchenIslandMaterial);
export const selectKitchenIslandCoverPanelId = selectOptionById('coverpanels', selectKitchenIslandCoverPanel, parameters.kitchenIslandMaterial);
export const selectKitchenIslandEdgeId = selectOptionById('edges', selectKitchenIslandEdge);
export const selectKitchenIslandThicknessId = selectOptionById('thicknesses', selectKitchenIslandThickness);
export const selectKitchenIslandOverhang = selectParameter(parameters.kitchenIslandOverhang);
export const selectKitchenIslandOverhangId = selectOptionById('overhangs', parameters.kitchenIslandOverhang);
export const selectKitchenIslandOverhangSide = selectParameter(parameters.kitchenIslandOverhangSide);
export const selectKitchenIslandOverhangSideId = selectOptionById('overhangSides', parameters.kitchenIslandOverhangSide, parameters.kitchenIsland);
export const selectEdgeId = selectOptionById('edges', parameters.edge);
export const selectThicknessId = selectOptionById('thicknesses', parameters.thickness);
export const selectSplashback = selectParameter(parameters.splashback);
export const selectDrainerGroovesId = selectOptionById('drainerGrooves', parameters.drainerGrooves);
export const selectSinkFasteningId = selectOptionById('sinkFastenings', parameters.sinkFastening);
export const selectWallPanelMaterialId = selectOptionById('wallPanels', selectWallPanelMaterial);
export const selectWallEdgingStripMaterialId = selectOptionById('wallEdgingStrip', selectWallEdgingStripMaterial);
export const selectWallEdgingStripCustomizableMaterialId = selectOptionById('wallEdgingStripCustomizable', selectWallEdgingStripCustomizableMaterial);
export const selectNoMaterialWallPanelMaterialId = selectOptionById('wallPanels', selectWallPanelMaterial);
export const selectExpressionId = selectOptionById('expressions', selectExpression, parameters.material);
export const selectCoverPanelId = selectOptionById('coverpanels', selectCoverPanel, parameters.material);
export const selectWallPanelExpressionId = selectOptionById('expressions', selectWallPanelExpression, parameters.wallPanelMaterial);
export const selectOverhang = selectParameter(parameters.overhang);
export const selectOverhangId = selectOptionById('overhangs', parameters.overhang);
export const selectNoMaterialOverhangId = selectOptionById('overhangs', parameters.overhang);
export const selectOverhangSide = selectParameter(parameters.overhangSide);
export const selectOverhangSideId = selectOptionById('overhangSides', parameters.overhangSide, parameters.plannedShape);

function decentralizedEdgeDefaultSelection(state, material, expression, decentralizedEdge, worktopOrKitchenIslandEdge){
  const isMaterialQuartzOrCeramic = (material.get('id') === 'quartz' || material.get('id') === 'ceramic');
  const edgeExists = state.getIn(['options', 'edges']).find((edge) => edge.get('id') === decentralizedEdge);
  const isExpressionDecentralized = expression.getIn(['thicknesses', '0', 'priceByArea']);
  const lastSelectionDoneByDefault = state.getIn(['plan', worktopOrKitchenIslandEdge, 'selectionType']) === 'default';
  return (isMaterialQuartzOrCeramic && edgeExists && isExpressionDecentralized && lastSelectionDoneByDefault)
}

function selectMaterial(state, material, selectionType) {
  if (state.getIn(['plan', parameters.material, 'value']) === material) {
    return state;
  }

  const { value: expression, selectionType: expressionSelectionType } = getPreviousSelection(state, material, 'expressionSelections', 'expressions');
  if (decentralizedEdgeDefaultSelection(state, material, expression, "chamfered", "edge")) {
    state = selectEdgeId(state, "chamfered", "defaultDecentralized");
  } 

  let withMaterial = setParameter(state, parameters.material, material, selectionType);
  withMaterial = selectExpression(withMaterial, expression, expressionSelectionType);
  const useCoverPanels = state.getIn(['plan', 'material', 'value', 'coverpanels']);
  if(useCoverPanels){
    if(useCoverPanels.size > 0){
      const { value: coverpanel, selectionType: coverPanelSelectionType } = getPreviousSelection(state, material, 'coverpanelSelections', 'coverpanels');
      withMaterial = selectCoverPanel(withMaterial, coverpanel, coverPanelSelectionType);
    }
  }
  return withMaterial;
}

function selectExpression(state, expression, selectionType) {
  const materialId = getParameterId(state, parameters.material);
  return setParameter(state, parameters.expression, expression, selectionType)
    .setIn(['plan', 'expressionSelections', materialId], expression);
}
function selectCoverPanel(state, coverpanel, selectionType) {
  const materialId = getParameterId(state, parameters.material);
  let newState = setParameter(state, parameters.coverPanel, coverpanel, selectionType)
    .setIn(['plan', 'coverpanelSelections', materialId], coverpanel);
  return newState;
}


function selectKitchenIslandMaterial(state, material, selectionType, kitchenIslandSameAsWorktop = false) {
  if (state.getIn(['plan', parameters.kitchenIslandMaterial, 'value']) === material) {
    return state;
  }

  const { value: expression, selectionType: expressionSelectionType } = getPreviousSelection(state, material, 'kitchenIslandExpressionSelections', 'expressions');

  if (decentralizedEdgeDefaultSelection(state, material, expression, "chamfered", "kitchenIslandEdge")) {
    state = selectKitchenIslandEdgeId(state, "chamfered", "defaultDecentralized");
  }
  let withMaterial = setParameter(state, parameters.kitchenIslandMaterial, material, selectionType);
  withMaterial = selectKitchenIslandExpression(withMaterial, expression, expressionSelectionType, kitchenIslandSameAsWorktop);
  
  const useCoverPanels = state.getIn(['plan', 'material', 'value', 'coverpanels']);
  if (useCoverPanels) {
    if (useCoverPanels.size > 0) {
      const { value: coverpanel, selectionType: coverPanelSelectionType } = getPreviousSelection(state, material, 'kitchenIslandCoverPanelSelections', 'coverpanels');
      withMaterial = selectKitchenIslandCoverPanel(withMaterial, coverpanel, coverPanelSelectionType);
    }
  }
  return withMaterial;
}

function selectKitchenIslandExpression(state, expression, selectionType, kitchenIslandSameAsWorktop = false) {
  const materialId = getParameterId(state, parameters.kitchenIslandMaterial);

  return setParameter(state, parameters.kitchenIslandExpression, expression, selectionType)
    .setIn(['plan', 'kitchenIslandExpressionSelections', materialId], expression)
    .setIn(['plan', 'kitchenIslandSameAsWorktop'], kitchenIslandSameAsWorktop);
}

function selectKitchenIslandCoverPanel(state, coverpanel, selectionType) {
  const materialId = getParameterId(state, parameters.material);
  let newState = setParameter(state, parameters.kitchenIslandCoverPanel, coverpanel, selectionType)
    .setIn(['plan', 'kitchenIslandCoverPanelSelections', materialId], coverpanel);
  return newState;
}

function selectKitchenIslandEdge(state, edge, selectionType, kitchenIslandSameAsWorktop = false) {
  return setParameter(state, parameters.kitchenIslandEdge, edge, selectionType)
    .setIn(['plan', 'kitchenIslandSameAsWorktop'], kitchenIslandSameAsWorktop);
}

function selectKitchenIslandThickness(state, thickness, selectionType, kitchenIslandSameAsWorktop = false) {
  return setParameter(state, parameters.kitchenIslandThickness, thickness, selectionType)
    .setIn(['plan', 'kitchenIslandSameAsWorktop'], kitchenIslandSameAsWorktop);
}

function setKitchenIslandDoubleSided(state) {
  const doubleSided = state.getIn(['plan', 'kitchenIslandDoubleSided']);
  return state.setIn(['plan', 'kitchenIslandDoubleSided'], !doubleSided);
}

function selectKitchenIslandSplashBack(state) {
  const splashBack = state.getIn(['plan', 'kitchenIslandSplashBack']);
  return state.setIn(['plan', 'kitchenIslandSplashBack'], !splashBack);
}

function setDoubleSided(state) {
  const doubleSided = state.getIn(['plan', 'doubleSided']);
  return state.setIn(['plan', 'doubleSided'], !doubleSided);
}

export function selectSplashbackId(state, splashbackId, selectionType) {
  let newState = state;
  if (splashbackId !== WALL_PANEL_ID) {
    newState = state.withMutations((tempState) => {
      return parameters.wallPanelOperations.reduce((reducedState, param) => resetNumericalParameter(reducedState, param), tempState);
    });

    // Unavailable wall panel material or expression option
    if (!(state.getIn(['plan', 'wallPanelMaterial', 'value']) && state.getIn(['plan', 'wallPanelExpression', 'value']))) {
      newState = selectWallPanelMaterial(newState, state.getIn(['options', 'wallPanels', 0]), SELECTION_TYPE_DEFAULT);
    }
  }

  const splashbackOption = findById(newState, 'splashbacks', splashbackId);
  return selectSplashback(newState, splashbackOption, selectionType);
}


function selectKitchIsland(state, kitchenIsland, selectionType, flipped) {
  const hasWallLocations = (state.getIn(['plan', 'kitchenIslandWallLocation', 'value']).size > 0) || (state.getIn(['plan', 'kitchenIslandWallLocation', 'value']).length > 0);
  const kitchenIslandId = kitchenIsland.get('id');
  const shape = findById(state, 'kitchenIslands', kitchenIslandId);
  if ((selectionType === 'default' && !hasWallLocations) || (selectionType === 'user' && !hasWallLocations)) {
    // Reset wall location selection
    const noWalls = state.getIn(['options', 'kitchenIslands', 1, 'kitchenIslandWallLocations']).find((wallLocation) => wallLocation.get('id') === 'no-walls');
    const tmpState = selectKitchenIslandWallLocation(state, noWalls, SELECTION_TYPE_DEFAULT);
    return setParameter(tmpState, parameters.kitchenIsland, shape, selectionType, {
      flipped,
    })
      .withMutations((tempState) => parameters.kitchenIslandOperations.reduce((reducedState, param) => resetNumericalParameter(reducedState, param), tempState)).setIn(['kitchenIslandFlipped', kitchenIslandId], flipped);
  }
  return setParameter(state, parameters.kitchenIsland, shape, selectionType, {
    flipped,
  })
    .withMutations((tempState) => parameters.kitchenIslandOperations.reduce((reducedState, param) => resetNumericalParameter(reducedState, param), tempState)).setIn(['kitchenIslandFlipped', kitchenIslandId], flipped);
}

function selectWallPanelMaterial(state, material, selectionType, matchWorktop = false) {
  const { value: expression, selectionType: expressionSelectionType } = getPreviousSelection(state, material, 'wallPanelExpressionSelections', 'expressions');
  const withMaterial = setParameter(state, parameters.wallPanelMaterial, material, selectionType);
  return selectWallPanelExpression(withMaterial, expression, expressionSelectionType, matchWorktop);
}

function selectWallEdgingStripMaterial(state, material, selectionType) {
  if(material) {
    const hasWallEdgingStripCustomizable = (state.getIn(['options', 'wallEdgingStripCustomizable']).size > 0 || state.getIn(['options', 'wallEdgingStripCustomizable']).length > 0);    
    const materialExists = state.getIn(['options', 'wallEdgingStripCustomizable']).find((wallEdgingStripCustomizableMaterial) => wallEdgingStripCustomizableMaterial.get('id') === material.get('id'));
    let newState = setParameter(state, parameters.wallEdgingStripMaterial, material, selectionType);

    // Set wallEdgingStripcustomizable material same as wallEdgingStrip material only if material available
    if (hasWallEdgingStripCustomizable && materialExists) {
      const wallEdgingStripCustomizableOption = findById(state, 'wallEdgingStripCustomizable', material.get('id'));
      return setParameter(newState, parameters.wallEdgingStripCustomizableMaterial, wallEdgingStripCustomizableOption, selectionType);
    }else {
      return newState;
    }
  }
  return state;
}

function selectWallEdgingStripCustomizableMaterial(state, material, selectionType) {
  if(material) {
    const hasWallEdgingStrip = (state.getIn(['options', 'wallEdgingStrip']).size > 0 || state.getIn(['options', 'wallEdgingStrip']).length > 0);
    const materialExists = state.getIn(['options', 'wallEdgingStrip']).find((wallEdgingStripMaterial) => wallEdgingStripMaterial.get('id') === material.get('id'));
    let newState =  setParameter(state, parameters.wallEdgingStripCustomizableMaterial, material, selectionType);

    // Set wallEdgingStrip material same as wallEdgingStripCustomizable material only if material available
    if(hasWallEdgingStrip && materialExists) {
      const wallEdgingStripOption = findById(state, 'wallEdgingStrip', material.get('id'));
      return setParameter(newState, parameters.wallEdgingStripMaterial, wallEdgingStripOption, selectionType);
    }else {
      return newState;
    }
  }
  return state;
}


function selectWallPanelExpression(state, expression, selectionType, matchWorktop = false) {
  const materialId = getParameterId(state, parameters.wallPanelMaterial);

  return setParameter(state, parameters.wallPanelExpression, expression, selectionType)
    .setIn(['plan', 'wallPanelExpressionSelections', materialId], expression)
    .setIn(['plan', 'wallPanelMatchWorktop'], matchWorktop);
}

export function selectSinkMaterialId(state, sinkMaterialId, selectionType) {
  const previouslySelectedSinkModel = state.getIn(['plan', 'sinkModelSelections', sinkMaterialId]);

  const sinkMaterialOption = findById(state, 'sinks', sinkMaterialId);
  const withSinkMaterial = setParameter(state, parameters.sinkMaterial, sinkMaterialOption, selectionType);

  let withResetSinkParameters;
  if (sinkMaterialId === 'noSink') {
    const withNoDrainerGrooves = selectDrainerGroovesId(withSinkMaterial, 'none', SELECTION_TYPE_DEFAULT);
    withResetSinkParameters = selectSinkFasteningId(withNoDrainerGrooves, 'topMounted', SELECTION_TYPE_DEFAULT);
  }

  // set first model with image as default (ie handle NORRSJ�N 21x44 cm)
  const sinkModel = sinkMaterialOption ? previouslySelectedSinkModel || sinkMaterialOption.get('models').find((model) => model.get('displayImage')) || sinkMaterialOption.getIn(['models', 0]) : null;
  const sinkModelId = sinkModel && sinkModel.get('id');
  return selectSinkModelId(withResetSinkParameters || withSinkMaterial, sinkModelId, SELECTION_TYPE_DEFAULT);
}

export function selectSinkModelId(state, sinkModelId, selectionType) {
  const sinkModel = findChildById(state, parameters.sinkMaterial, 'models', sinkModelId);
  const sinkMaterialId = getParameterId(state, parameters.sinkMaterial);
  const withSinkModel = setParameter(state, parameters.sinkModel, sinkModel, selectionType)
    .setIn(['plan', 'sinkModelSelections', sinkMaterialId], sinkModel);

  if (sinkModel) {
    if (sinkModel.get('requiresTapHole')) {
      if (getParameterId(withSinkModel, parameters.sinkMaterial) === 'noSink') return withSinkModel;
      else if (withSinkModel.getIn(['plan', parameters.tapHole, 'value']) === 0) {
        return setParameter(withSinkModel, parameters.tapHole, 1, SELECTION_TYPE_DEFAULT);
      }
    } else if (withSinkModel.getIn(['plan', parameters.tapHole, 'selectionType']) === SELECTION_TYPE_DEFAULT) {
      return setParameter(withSinkModel, parameters.tapHole, 0, SELECTION_TYPE_DEFAULT);
    }
  }

  return withSinkModel;
}

function changeCuts(state, amount, parameter) {
  const changedCuts = state.getIn(['plan', parameter, 'value']) + amount;
  if (changedCuts >= 0) {
    return setParameter(state, parameter, changedCuts, SELECTION_TYPE_USER);
  } else {
    return state;
  }
}

function changeParameterSelectionType(state, parameter, id) {
  if (parameters.allParameters.indexOf(parameter) < 0) {
    throw new Error(`Can't find path for parameter ${parameter}`);
  }

  const paramId = getParameterId(state, parameter);
  const type = state.getIn(['plan', parameter, 'selectionType']);

  if (paramId === id && type === SELECTION_TYPE_AUTOMATIC) {
    return state.setIn(['plan', parameter, 'selectionType'], SELECTION_TYPE_DEFAULT);
  }

  return state;
}

export function resetPlan(state, force) {
  // Someone pressed create new before we have loaded rules and items.
  // Default options will be selected when options are loaded.
  if (!force && state.get('loading')) {
    return state;
  } else {
    const withEmptyPlan = state.withMutations((temp) => temp.set('plan', planInitialState)
                                                          .set('shapeFields', Map())
                                                          .set('overhangFields', Map())
                                                          .set('kitchenIslandOverhangFields', Map())
                                                          .set('shapesFlipped', Map())
                                                          .set('kitchenIslandFlipped', Map())
                                                          .set('planName', null)
                                                          .set('planCode', null)
                                                          .setIn(['previous', 'plan'], null)
                                                          .setIn(['previous', 'shapesFlipped'], null)
                                                          .setIn(['previous', 'kitchenIslandFlipped'], null)
                                                          .setIn(['previous', 'shapeFields'], null)
                                                        );
    return selectDefaultOptions(withEmptyPlan);
  }
}

function planReducer(state, action) {
  // All NO_MATERIAL_{ type of action here } is used to execute the same type of action as the original action.
  // The difference is that the NO_MATERIAL_ actions is not included in revertToLast.js
  // When these actions are used, they will not count as an action that can be reversed in revertToLast.js
  switch (action.type) {
    case RESET_PLAN:
    case OPTIONS_LOADED:
      return resetPlan(state);
    case SELECT_MATERIAL:
      return selectMaterialId(state, action.materialId, SELECTION_TYPE_USER);
    case SELECT_EXPRESSION:
      return selectExpressionId(state, action.expressionId, SELECTION_TYPE_USER);
    case SELECT_COVER_PANEL:
      return selectCoverPanelId(state, action.coverPanelId, SELECTION_TYPE_USER);
    case SELECT_EDGE:
      return selectEdgeId(state, action.edgeId, SELECTION_TYPE_USER);
    case SELECT_WALL_LOCATION:
      return selectWallLocationId(state, action.wallLocationId, SELECTION_TYPE_USER);
    case SELECT_KITCHEN_ISLAND_WALL_LOCATION:
      return selecKitchenIslandWallLocationId(state, action.wallLocationId, SELECTION_TYPE_USER);
    case SELECT_KITCHEN_ISLAND:
      return selectKitchenIslandId(state, action.kitchenIslandId, SELECTION_TYPE_USER, !!action.flipped);
    case SELECT_OVERHANG:
      return selectOverhangId(state, action.overhangId, SELECTION_TYPE_USER);
    case SELECT_OVERHANG_SIDE:
      return selectOverhangSideId(state, action.sideId, SELECTION_TYPE_USER);
    case SELECT_NO_MATERIAL_OVERHANG:
      return selectNoMaterialOverhangId(state, action.overhangId, SELECTION_TYPE_USER);
    case SELECT_THICKNESS:
      return selectThicknessId(state, action.thicknessId, SELECTION_TYPE_USER);
    case SELECT_SPLASHBACK:
      return selectSplashbackId(state, action.splashbackId, SELECTION_TYPE_USER);
    case SELECT_WALL_PANEL_MATERIAL:
      return selectWallPanelMaterialId(state, action.materialId, SELECTION_TYPE_USER);
    case SELECT_WALL_EDGING_STRIP_MATERIAL:
      return selectWallEdgingStripMaterialId(state, action.materialId, SELECTION_TYPE_USER);
    case SELECT_WALL_EDGING_STRIP_CUSTOMIZABLE_MATERIAL:
      return selectWallEdgingStripCustomizableMaterialId(state, action.materialId, SELECTION_TYPE_USER);
    case SELECT_NO_MATERIAL_WALL_PANEL_MATERIAL:
      return selectWallPanelMaterialId(state, action.materialId, SELECTION_TYPE_USER);
    case SELECT_WALL_PANEL_EXPRESSION:
      return selectWallPanelExpressionId(state, action.expressionId, SELECTION_TYPE_USER);
    case SELECT_SINK_MATERIAL:
      return selectSinkMaterialId(state, action.sinkMaterialId, SELECTION_TYPE_USER);
    case SELECT_SINK_MODEL:
      return selectSinkModelId(state, action.sinkModelId, SELECTION_TYPE_USER);
    case SELECT_SINK_FASTENING:
      return selectSinkFasteningId(state, action.sinkFasteningId, SELECTION_TYPE_USER);
    case SELECT_DRAINER_GROOVES:
      return selectDrainerGroovesId(state, action.drainerGroovesId, SELECTION_TYPE_USER);
    case CHANGE_SHAPE:
      return changeShape(state, action.shapeId, SELECTION_TYPE_USER, action.measurements, !!action.flipped);
    case NO_MATERIAL_CHANGE_SHAPE:
      return noMaterialChangeShape(state, action.shapeId, SELECTION_TYPE_USER, action.measurements, !!action.flipped);
    case CHANGE_CUTOUTS:
      return changeCuts(state, action.amount, parameters.cutout);
    case CHANGE_FREESTANDING_HOBS:
      return changeCuts(state, action.amount, parameters.freestandingHob);
    case CHANGE_SINGLE_CUTS:
      return changeCuts(state, action.amount, parameters.singleCut);
    case CHANGE_DOUBLE_CUTS:
      return changeCuts(state, action.amount, parameters.doubleCut);
    case CHANGE_TRIPLE_CUTS:
      return changeCuts(state, action.amount, parameters.tripleCut);
    case CHANGE_HIGH_CABINET_JOINTS:
      return changeCuts(state, action.amount, parameters.highCabinetJoint);
    case CHANGE_ROUNDED_CORNERS:
      return changeCuts(state, action.amount, parameters.roundedCorner);
    case CHANGE_INVERTED_CORNERS:
      return changeCuts(state, action.amount, parameters.invertedCorner);
    case CHANGE_TAP_HOLES:
      return changeCuts(state, action.amount, parameters.tapHole);
    case CHANGE_WALL_PANEL_ROUND_CUTS_SMALL:
      return changeCuts(state, action.amount, parameters.wallPanelRoundCutSmall);
    case CHANGE_WALL_PANEL_ROUND_CUTS_BIG:
      return changeCuts(state, action.amount, parameters.wallPanelRoundCutBig);
    case CHANGE_WALL_PANEL_SQUARE_CUTS:
      return changeCuts(state, action.amount, parameters.wallPanelSquareCut);
    case CHANGE_WALL_PANEL_SINGLE_CUTS:
      return changeCuts(state, action.amount, parameters.wallPanelSingleCut);
    case CHANGE_WALL_PANEL_DOUBLE_CUTS:
      return changeCuts(state, action.amount, parameters.wallPanelDoubleCut);
    case CHANGE_WALL_PANEL_TRIPLE_CUTS:
      return changeCuts(state, action.amount, parameters.wallPanelTripleCut);
    case CHANGE_KITCHEN_ISLAND_SINGLE_CUTS:
      return changeCuts(state, action.amount, parameters.kitchenIslandSingleCut);
    case CHANGE_KITCHEN_ISLAND_ROUNDED_CORNERS:
      return changeCuts(state, action.amount, parameters.kitchenIslandRoundedCorner);
    case CHANGE_KITCHEN_ISLAND_SQUARE_CUTS:
      return changeCuts(state, action.amount, parameters.kitchenIslandSquareCut);
    case SELECT_KITCHEN_ISLAND_MATERIAL:
      return selectKitchenIslandMaterialId(state, action.materialId, SELECTION_TYPE_USER);
    case SELECT_NO_MATERIAL_KITCHEN_ISLAND_MATERIAL:
      return selectNoMaterialKitchenIslandMaterialId(state, action.materialId, SELECTION_TYPE_USER);
    case SELECT_KITCHEN_ISLAND_EXPRESSION:
      return selectKitchenIslandExpressionId(state, action.expressionId, SELECTION_TYPE_USER);
    case SELECT_KITCHEN_ISLAND_COVER_PANEL:
      return selectKitchenIslandCoverPanelId(state, action.coverPanelId, SELECTION_TYPE_USER);
    case SELECT_KITCHEN_ISLAND_EDGE:
      return selectKitchenIslandEdgeId(state, action.edgeId, SELECTION_TYPE_USER);
    case SELECT_KITCHEN_ISLAND_THICKNESS:
      return selectKitchenIslandThicknessId(state, action.thicknessId, SELECTION_TYPE_USER);
    case SELECT_KITCHEN_ISLAND_OVERHANG:
      return selectKitchenIslandOverhangId(state, action.overhangId, SELECTION_TYPE_USER);
    case SELECT_KITCHEN_ISLAND_OVERHANG_SIDE:
      return selectKitchenIslandOverhangSideId(state, action.overhangSideId, SELECTION_TYPE_USER);
    case SELECT_KITCHEN_ISLAND_SAME_AS_WORKTOP:
      return state.setIn(['plan', 'kitchenIslandSameAsWorktop'], action.sameAsWorktop);
    case SELECT_NO_MATERIAL_KITCHEN_ISLAND_SAME_AS_WORKTOP:
      return state.setIn(['plan', 'kitchenIslandSameAsWorktop'], action.sameAsWorktop);
    case SELECT_WALL_PANEL_MATCH_WORKTOP:
      return state.setIn(['plan', 'wallPanelMatchWorktop'], action.matchWorktop);
    case SET_DOUBLE_SIDED:
      return setDoubleSided(state);
    case SELECT_NO_MATERIAL_WALL_PANEL_MATCH_WORKTOP:
      return state.setIn(['plan', 'wallPanelMatchWorktop'], action.matchWorktop);
    case SET_KITCHEN_ISLAND_DOUBLE_SIDED:
      return setKitchenIslandDoubleSided(state);
    case SELECT_KITCHEN_ISLAND_SPLASH_BACK:
      return selectKitchenIslandSplashBack(state);
    case PARAMETER_SEEN:
      return changeParameterSelectionType(state, action.parameter, action.id);

    case SET_PLAN_NAME:
      return state.set('planName', action.name);
    case SET_PLAN_CODE:
      return state.set('planCode', action.code).setIn(['plan', 'hasChanges'], false);
    case RESET_NUMERICAL_PARAMETER:
      return resetNumericalParameter(state, action.parameter);
    case NO_MATERIAL_RESET_NUMERICAL_PARAMETER:
      return noMaterialresetNumericalParameter(state, action.parameter);
    case RESET_WORKTOP_SHAPE_MEASUREMENTS:
      return resetWorktopShapeMeasurements(state, action.shapeId, action.splashbackId);
    case RESET_PREVIOUS_SINK_MODEL:
      return state.setIn(['plan', 'sinkModelSelections'], Map());
    default:
      return state;
  }
}

export default planReducer;
